import { useEffect, useState } from "react";
import { X, Search, FileText, Upload, Paperclip, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Modal picker per collegare un documento a un financial_item.
 * - Filtri: ricerca titolo + categoria (opz.)
 * - Badge documenti già collegati altrove
 * - CTA "Carica nuovo documento" → apre input file → auto-select del nuovo doc
 * SI-008: nessun contenuto documento mostrato qui, solo metadata.
 */
export default function DocumentPickerModal({ open, onClose, onSelect, currentId = null }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 100 };
        if (category) params.category = category;
        const res = await api.docsList(params);
        setDocs(res.items || res || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossibile caricare i documenti");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, category]);

  if (!open) return null;

  const filtered = docs.filter((d) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (d.title || d.original_filename || "").toLowerCase().includes(needle)
        || (d.category || "").toLowerCase().includes(needle);
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const res = await api.docsUpload(files);
      // Backend returns { uploaded: [...], duplicates: [...] }
      const created = (res.uploaded && res.uploaded[0]) || (res.duplicates && res.duplicates[0]);
      if (!created) throw new Error("Upload response vuota");
      toast.success(res.uploaded?.length ? "Documento caricato · in analisi IA" : "Documento già presente · riutilizzato");
      onSelect(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload fallito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="document-picker-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-picker-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
          <h3 id="doc-picker-title" className="font-display text-xl font-semibold flex items-center gap-2">
            <Paperclip className="h-4 w-4" /> Allega documento
          </h3>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="doc-picker-close">✕</button>
        </div>

        <div className="p-4 border-b space-y-3" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex-1 flex items-center gap-2 pxos-input">
              <Search className="h-4 w-4 opacity-60" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per titolo o categoria…"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="doc-picker-search"
              />
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="pxos-input sm:max-w-[180px]"
              data-testid="doc-picker-category"
            >
              <option value="">Tutte le categorie</option>
              <option value="fattura">Fattura</option>
              <option value="ricevuta">Ricevuta</option>
              <option value="estratto_conto">Estratto conto</option>
              <option value="documento_identita">Documento identità</option>
              <option value="contratto">Contratto</option>
              <option value="referto_medico">Referto medico</option>
              <option value="altro">Altro</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-[var(--pxos-accent-dark)] hover:underline">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Caricamento…" : "Carica nuovo documento"}
            <input
              type="file"
              hidden
              multiple={false}
              onChange={handleUpload}
              disabled={uploading}
              accept="application/pdf,image/*"
              data-testid="doc-picker-upload-input"
            />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-2" data-testid="doc-picker-list">
          {loading ? (
            <div className="p-8 text-center opacity-70 text-sm">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Caricamento documenti…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center opacity-70 text-sm">
              Nessun documento trovato.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
              {filtered.map((d) => {
                const isLinkedElsewhere = d.linked_financial_item_id && d.linked_financial_item_id !== currentId;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(d)}
                      className="w-full text-left px-3 py-3 hover:bg-[var(--pxos-surface-soft)] flex items-start gap-3 rounded-lg"
                      data-testid={`doc-picker-item-${d.id}`}
                    >
                      <FileText className="h-4 w-4 mt-0.5 opacity-60 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {d.title || d.original_filename || "Senza titolo"}
                        </div>
                        <div className="text-[11px] opacity-60 flex items-center gap-2 mt-0.5 flex-wrap">
                          {d.category && (
                            <span className="uppercase tracking-widest">{d.category}</span>
                          )}
                          {d.uploaded_at && (
                            <span>{new Date(d.uploaded_at).toLocaleDateString("it-IT")}</span>
                          )}
                          {isLinkedElsewhere && (
                            <span className="px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "#FFF0F0", color: "#B42318" }}>
                              già collegato
                            </span>
                          )}
                          {d.linked_financial_item_id === currentId && currentId && (
                            <span className="px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "#E7F3E9", color: "#1F7A2C" }}>
                              collegato qui
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-3 border-t flex justify-end" style={{ borderColor: "var(--pxos-border)" }}>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border px-4 py-2 text-xs font-semibold"
            data-testid="doc-picker-cancel"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
